import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GlobeAltIcon, LocationMarkerIcon, SearchIcon } from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";

const fallbackCountries = [
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Ireland",
  "Germany",
  "New Zealand",
  "France",
];

export default function Destinations() {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFacets = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/consultancies/facets`);
        const data = res.ok ? await res.json() : {};
        setCountries(Array.isArray(data.countries) ? data.countries.filter(Boolean) : []);
        setCities(Array.isArray(data.cities) ? data.cities.filter(Boolean) : []);
      } catch (err) {
        console.error("Destinations facets error", err);
      } finally {
        setLoading(false);
      }
    };

    loadFacets();
  }, []);

  const visibleCountries = useMemo(() => {
    const source = countries.length ? countries : fallbackCountries;
    return Array.from(new Set(source)).sort((a, b) => a.localeCompare(b));
  }, [countries]);

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="border-b border-white/10 bg-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:py-16">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-300">
              Study Destinations
            </p>
            <h1 className="mt-4 text-[clamp(2rem,8vw,4rem)] font-semibold leading-tight tracking-tight">
              Explore countries and find consultancies that process them.
            </h1>
            <p className="mt-5 text-base leading-7 text-slate-300 sm:text-lg">
              Choose a destination to see overseas education consultancies that list that country in AbroadJacks.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/consultancies"
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200"
              >
                Browse All Consultancies
              </Link>
              <Link
                to="/about"
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                How AbroadJacks Works
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-300">Countries</p>
            <h2 className="mt-2 text-2xl font-semibold">Popular processing countries</h2>
          </div>
          <p className="text-sm text-slate-400">
            {loading ? "Loading destinations..." : `${visibleCountries.length} destinations available`}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleCountries.map((country) => (
            <Link
              key={country}
              to={`/consultancies?country=${encodeURIComponent(country)}`}
              className="group rounded-lg border border-white/10 bg-white/[0.055] p-5 transition hover:-translate-y-0.5 hover:border-cyan-300/35 hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-cyan-300/40"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-300/12 text-cyan-200">
                  <GlobeAltIcon className="h-6 w-6" />
                </span>
                <span className="text-xs font-semibold text-cyan-200">View</span>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{country}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                See consultancies that mention {country} as a processing destination.
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-white/[0.03]">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-8 sm:px-6 md:grid-cols-2">
          <div className="rounded-lg border border-white/10 bg-slate-950/70 p-5">
            <SearchIcon className="h-6 w-6 text-cyan-200" />
            <h3 className="mt-4 text-lg font-semibold">Search by country</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              Use country filters on the consultancy page to compare listed services, locations, offers, and trust status.
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-slate-950/70 p-5">
            <LocationMarkerIcon className="h-6 w-6 text-cyan-200" />
            <h3 className="mt-4 text-lg font-semibold">Combine with location</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              Filter by city or state after choosing a country to find consultancies near you.
            </p>
            {cities.length > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                Cities indexed: {cities.slice(0, 5).join(", ")}
                {cities.length > 5 ? "..." : ""}
              </p>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
